import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Edit, Trash2, CheckCircle2, XCircle, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { SelectIdea } from "@db/schema";
import { useState } from "react";
import { Link } from "wouter";
import { IdeaDialog } from "@/components/idea-dialog";
import { colors } from "@/lib/colors";
import { UserMenu } from "@/components/user-menu";

type SortField = 'title' | 'tag' | 'votes' | 'createdAt' | 'status' | 'comments' | 'username';
type SortDirection = 'asc' | 'desc';
type Status = 'Needs Review' | 'Under Consideration' | 'In Progress' | 'Rejected' | 'Complete';
type Tag = 'Reduce' | 'Reform' | 'Advance' | 'Expand';

const tagColors = {
  Reduce: "bg-[#5a8ba7]/10 text-[#5a8ba7]",
  Reform: "bg-[#4b4f54]/10 text-[#4b4f54]",
  Advance: "bg-[#dc582a]/10 text-[#dc582a]",
  Expand: "bg-[#799b3e]/10 text-[#799b3e]",
} as const;

const statusColors = {
  'Needs Review': 'bg-red-500 text-white',
  'Under Consideration': 'bg-[#5a8ba7] text-white',
  'In Progress': 'bg-[#799b3e] text-white',
  'Rejected': 'bg-[#4b4f54]/50 text-white',
  'Complete': 'bg-[#4b4f54] text-white',
} as const;

const statusTextColors = {
  'Needs Review': 'text-red-500',
  'Under Consideration': 'text-[#5a8ba7]',
  'In Progress': 'text-[#799b3e]',
  'Rejected': 'text-[#4b4f54]/50',
  'Complete': 'text-[#4b4f54]',
} as const;

export default function AdminDashboard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedIdea, setSelectedIdea] = useState<SelectIdea | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tagChangeDialog, setTagChangeDialog] = useState<{
    open: boolean;
    ideaId: number;
    oldTag: Tag;
    newTag: Tag;
    ideaTitle: string;
  }>({
    open: false,
    ideaId: 0,
    oldTag: 'Reduce',
    newTag: 'Reduce',
    ideaTitle: ''
  });

  const { data: ideas, isLoading } = useQuery({
    queryKey: ["/api/admin/ideas", sortField, sortDirection],
    queryFn: async () => {
      const res = await fetch(`/api/admin/ideas?sort=${sortField}&direction=${sortDirection}`);
      if (!res.ok) throw new Error("Failed to fetch ideas");
      return res.json();
    },
  });

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return <ArrowUpDown className="h-4 w-4 ml-2" />;
    return sortDirection === 'asc' ?
      <ArrowUp className="h-4 w-4 ml-2" /> :
      <ArrowDown className="h-4 w-4 ml-2" />;
  };

  const deleteMutation = useMutation({
    mutationFn: async (ideaId: number) => {
      const res = await fetch(`/api/admin/ideas/${ideaId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ideas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Idea deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete idea",
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ ideaId, status }: { ideaId: number; status: Status }) => {
      const res = await fetch(`/api/admin/ideas/${ideaId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      const data = await res.json();
      return data;
    },
    onSuccess: (updatedIdea) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ideas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      queryClient.refetchQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Status updated successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update status",
      });
    },
  });

  const tagMutation = useMutation({
    mutationFn: async ({ ideaId, tag }: { ideaId: number; tag: Tag }) => {
      const res = await fetch(`/api/admin/ideas/${ideaId}/tag`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag }),
      });
      if (!res.ok) throw new Error("Failed to update tag");
      const data = await res.json();
      return data;
    },
    onSuccess: (updatedIdea) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ideas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      queryClient.refetchQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update tag",
      });
    },
  });

  const handleTagChangeConfirm = () => {
    tagMutation.mutate({ 
      ideaId: tagChangeDialog.ideaId, 
      tag: tagChangeDialog.newTag 
    });
    setTagChangeDialog({ ...tagChangeDialog, open: false });
  };

  const handleTagChangeCancel = () => {
    setTagChangeDialog({ ...tagChangeDialog, open: false });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="w-full h-32 rounded-lg bg-white/50 animate-pulse" />
      </div>
    );
  }

  const getSortedIdeas = () => {
    if (!ideas) return [];
    
    return [...ideas].sort((a, b) => {
      if (sortField === 'votes') {
        const aVotes = a.upvotes - a.downvotes;
        const bVotes = b.upvotes - b.downvotes;
        return sortDirection === 'asc' ? aVotes - bVotes : bVotes - aVotes;
      }
      if (sortField === 'comments') {
        const aComments = a.commentCount || 0;
        const bComments = b.commentCount || 0;
        return sortDirection === 'asc' ? aComments - bComments : bComments - aComments;
      }
      if (sortField === 'createdAt') {
        return sortDirection === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      const aValue = a[sortField].toLowerCase();
      const bValue = b[sortField].toLowerCase();
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const sortedIdeas = getSortedIdeas();

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#5a8ba7]">
            Admin Dashboard
          </h1>
          <UserMenu />
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="!bg-[#5a8ba7]">
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center">
                    Title
                    {getSortIcon('title')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80"
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center">
                    Author
                    {getSortIcon('username')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80 text-center"
                  onClick={() => handleSort('tag')}
                >
                  <div className="flex items-center justify-center">
                    Tag
                    {getSortIcon('tag')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80 text-center"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center justify-center">
                    Status
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80 text-center"
                  onClick={() => handleSort('votes')}
                >
                  <div className="flex items-center justify-center">
                    Votes
                    {getSortIcon('votes')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80 text-center"
                  onClick={() => handleSort('comments')}
                >
                  <div className="flex items-center justify-center">
                    Comments
                    {getSortIcon('comments')}
                  </div>
                </TableHead>
                <TableHead
                  className="font-semibold text-white cursor-pointer hover:opacity-80 text-center"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center justify-center">
                    Created At
                    {getSortIcon('createdAt')}
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-white text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedIdeas.map((idea: SelectIdea) => (
                <TableRow
                  key={idea.id}
                  className={`hover:bg-gray-50 ${idea.status === 'Rejected' ? 'text-gray-400' : ''}`}
                >
                  <TableCell
                    className={`font-medium cursor-pointer ${idea.status === 'Rejected' ? 'text-gray-400' : ''}`}
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    {idea.title}
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    <span className="text-purple-600">
                      {idea.username || 'Anonymous'}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div 
                      className="flex justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        value={idea.tag}
                        onValueChange={(newTag: Tag) => {
                          if (newTag !== idea.tag) {
                            setTagChangeDialog({
                              open: true,
                              ideaId: idea.id,
                              oldTag: idea.tag,
                              newTag,
                              ideaTitle: idea.title
                            });
                          }
                        }}
                        disabled={tagMutation.isPending}
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs border-0 bg-transparent hover:bg-gray-50">
                          <SelectValue>
                            <span className={`px-2 py-1 rounded-full text-sm ${tagColors[idea.tag as keyof typeof tagColors]} ${idea.status === 'Rejected' ? 'opacity-50' : ''}`}>
                              {idea.tag}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Reduce">
                            <span className={`px-2 py-1 rounded-full text-sm ${tagColors.Reduce}`}>
                              Reduce
                            </span>
                          </SelectItem>
                          <SelectItem value="Reform">
                            <span className={`px-2 py-1 rounded-full text-sm ${tagColors.Reform}`}>
                              Reform
                            </span>
                          </SelectItem>
                          <SelectItem value="Advance">
                            <span className={`px-2 py-1 rounded-full text-sm ${tagColors.Advance}`}>
                              Advance
                            </span>
                          </SelectItem>
                          <SelectItem value="Expand">
                            <span className={`px-2 py-1 rounded-full text-sm ${tagColors.Expand}`}>
                              Expand
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-center cursor-pointer w-[200px] whitespace-nowrap"
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    <span className={`inline-block px-3 py-1 rounded-full text-sm min-w-[160px] whitespace-nowrap ${statusColors[(idea.status ?? 'Under Consideration') as Status]}`}>
                      {idea.status}
                    </span>
                  </TableCell>
                  <TableCell
                    className="text-center cursor-pointer"
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-green-600 font-medium">+{idea.upvotes}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-red-600 font-medium">-{idea.downvotes}</span>
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-center cursor-pointer"
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    {idea.commentCount || 0}
                  </TableCell>
                  <TableCell
                    className={`text-center cursor-pointer ${idea.status === 'Rejected' ? 'text-gray-400' : 'text-gray-500'}`}
                    onClick={() => {
                      setSelectedIdea(idea);
                      setDialogOpen(true);
                    }}
                  >
                    {new Date(idea.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-[#5a8ba7]">Delete Idea</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this idea? This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(idea.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <IdeaDialog
          idea={selectedIdea}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          showAdminControls={true}
        />

        {/* Tag Change Confirmation Dialog */}
        <AlertDialog open={tagChangeDialog.open} onOpenChange={(open) => !open && handleTagChangeCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#5a8ba7]">Confirm Tag Change</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to change the tag for "{tagChangeDialog.ideaTitle}" from{" "}
                <span className={`px-2 py-1 rounded-full text-sm ${tagColors[tagChangeDialog.oldTag]}`}>
                  {tagChangeDialog.oldTag}
                </span>{" "}
                to{" "}
                <span className={`px-2 py-1 rounded-full text-sm ${tagColors[tagChangeDialog.newTag]}`}>
                  {tagChangeDialog.newTag}
                </span>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleTagChangeCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleTagChangeConfirm}
                className="bg-[#5a8ba7] hover:bg-[#4a7a97]"
                disabled={tagMutation.isPending}
              >
                {tagMutation.isPending ? "Updating..." : "Confirm Change"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}